import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// Environment configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type AuthMode = 'bearer' | 'cookie';
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || 'bearer') as AuthMode;

let inMemoryAuthToken: string | null = null;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.defaults.withCredentials = AUTH_MODE === 'cookie';

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (AUTH_MODE === 'bearer') {
      const token = sessionStorage.getItem('authToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear in-memory token; caller/UI can decide what to do next.
      sessionStorage.removeItem('authToken');
    }
    return Promise.reject(error);
  }
);

// Authentication helper functions
export const setAuthToken = (token: string) => {
  sessionStorage.setItem('authToken', token);
};

export const getAuthToken = (): string | null => {
  return sessionStorage.getItem('authToken');;
};

export const removeAuthToken = () => {
  sessionStorage.removeItem('authToken');
};

export const getAuthMode = (): AuthMode => AUTH_MODE;

// API helper functions
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default apiClient;
